import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { useTaskActivity } from "../hooks/useWorkTasks";
import type { WorkTaskActivityType } from "@/api/workTasks";
import { useDateFormatter } from "@/utils/datetime";

interface Props {
  taskId: string;
}

const VERB: Record<WorkTaskActivityType, string> = {
  Created: "created the task",
  StatusChanged: "changed status",
  AssigneeChanged: "changed assignee",
  PriorityChanged: "changed priority",
  DueDateChanged: "changed due date",
  TitleChanged: "renamed the task",
  RelatedEntityChanged: "changed the linked entity",
  Reopened: "reopened the task",
  Deleted: "deleted the task",
  Restored: "restored the task",
  DescriptionChanged: "edited the description",
  TagsChanged: "updated tags",
  ParticipantsChanged: "updated participants",
};

export function TaskActivityLogPanel({ taskId }: Props) {
  const [open, setOpen] = useState(false);
  const q = useTaskActivity(taskId, 0, 50);
  const { dateTime } = useDateFormatter();

  return (
    <div className="rounded-md border border-border-light">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-space-2 px-space-3 py-space-2 text-left hover:bg-white-warm"
        aria-expanded={open}
      >
        <span className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
          Activity
        </span>
        {open ? (
          <ChevronDown size={14} aria-hidden className="text-gray-mid" />
        ) : (
          <ChevronRight size={14} aria-hidden className="text-gray-mid" />
        )}
      </button>
      {open && (
        <div className="px-space-3 py-space-2 border-t border-border-light bg-white-pure">
          {q.isLoading && <div className="text-small text-gray-mid">Loading…</div>}
          {q.data && q.data.data.length === 0 && (
            <div className="text-small text-gray-mid">No activity yet.</div>
          )}
          <ol className="flex flex-col gap-space-2 m-0 p-0 list-none">
            {q.data?.data.map((row) => (
              <li key={row.id} className="text-small text-onyx">
                <span className="font-semibold">{row.actorDisplayName}</span>{" "}
                {VERB[row.activityType] ?? row.activityType.toLowerCase()}
                {row.previousValue && row.newValue && (
                  <>
                    : <span className="text-gray-mid line-through">{row.previousValue}</span>{" "}
                    → <span>{row.newValue}</span>
                  </>
                )}
                {!row.previousValue && row.newValue && <>: {row.newValue}</>}
                <div className="text-label text-gray-mid">
                  {dateTime(row.createdAt)}
                </div>
              </li>
            ))}
          </ol>
          {q.data && q.data.totalCount > q.data.pageSize && (
            <div className="mt-space-2">
              <Button variant="tertiary" size="sm" type="button" onClick={() => q.refetch()}>
                Refresh
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
